import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';

import { MCPSavedProfileSchema, MCPServerSchema } from '@polyai/schemas';
import {
  deleteMcpProfile,
  deleteMcpServer,
  updateMcpConfig,
  upsertMcpProfile,
  upsertMcpServer,
} from '@polyai/stream/store/reducers/mcp-config/actions';
import type {
  MCPConfigSavedProfile,
  MCPConfigServer,
  MCPConfigState,
} from '@polyai/stream/store/reducers/mcp-config/types';

const serversAdapter = createEntityAdapter<MCPConfigServer, string>({
  selectId: server => server.id,
});
const profilesAdapter = createEntityAdapter<MCPConfigSavedProfile, string>({
  selectId: profile => profile.providerId,
});

const initialState: MCPConfigState = {
  servers: serversAdapter.getInitialState(),
  savedProfiles: profilesAdapter.getInitialState(),
};

const slice = createSlice({
  name: 'mcpConfig',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(updateMcpConfig, (state, action) => {
      const normalizedServers = Object.values(action.payload.serversById).map(
        server => MCPServerSchema.parse(server),
      );
      const normalizedProfiles = Object.values(
        action.payload.savedProfilesByProvider,
      ).map(profile => MCPSavedProfileSchema.parse(profile));

      serversAdapter.setAll(state.servers, normalizedServers);
      profilesAdapter.setAll(state.savedProfiles, normalizedProfiles);
    });

    builder.addCase(upsertMcpServer, (state, action) => {
      const server = MCPServerSchema.parse(action.payload.server);
      serversAdapter.upsertOne(state.servers, server);
    });

    builder.addCase(deleteMcpServer, (state, action) => {
      serversAdapter.removeOne(state.servers, action.payload.id);
    });

    builder.addCase(upsertMcpProfile, (state, action) => {
      const profile = MCPSavedProfileSchema.parse(action.payload.profile);
      profilesAdapter.upsertOne(state.savedProfiles, profile);
    });

    builder.addCase(deleteMcpProfile, (state, action) => {
      profilesAdapter.removeOne(state.savedProfiles, action.payload.providerId);
    });
  },
});

export {
  serversAdapter,
  profilesAdapter,
  updateMcpConfig,
  upsertMcpServer,
  deleteMcpServer,
  upsertMcpProfile,
  deleteMcpProfile,
};

export * from './types';
export default slice.reducer;
